import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { getFirstEnabledPath, isFeatureEnabled } from '@/lib/features'
import RestaurantLayout from '@/components/common/RestaurantLayout'
import SubscriptionExpiredScreen from '@/screens/auth/SubscriptionExpiredScreen'
import DashboardScreen from '@/screens/restaurant/DashboardScreen'
import AnalyticsScreen from '@/screens/restaurant/AnalyticsScreen'
import CustomersScreen from '@/screens/restaurant/CustomersScreen'
import SalesReportsScreen from '@/screens/restaurant/SalesReportsScreen'
import EditedBillsScreen from '@/screens/restaurant/EditedBillsScreen'
import MenuPerformanceScreen from '@/screens/restaurant/reports/MenuPerformanceScreen'
import PaymentModesScreen from '@/screens/restaurant/reports/PaymentModesScreen'
import GstReportScreen from '@/screens/restaurant/reports/GstReportScreen'
import DiscountsScreen from '@/screens/restaurant/reports/DiscountsScreen'
import StaffPerformanceScreen from '@/screens/restaurant/reports/StaffPerformanceScreen'
import TablePerformanceScreen from '@/screens/restaurant/reports/TablePerformanceScreen'
import AuditTrailScreen from '@/screens/restaurant/reports/AuditTrailScreen'
import ReceivablesScreen from '@/screens/restaurant/reports/ReceivablesScreen'
import DeliveryPerformanceScreen from '@/screens/restaurant/reports/DeliveryPerformanceScreen'
import ZReportScreen from '@/screens/restaurant/reports/ZReportScreen'
import OnlineOrdersScreen from '@/screens/restaurant/OnlineOrdersScreen'
import ParcelTakeawayOrdersScreen from '@/screens/restaurant/ParcelTakeawayOrdersScreen'
import ProfitAnalysisScreen from '@/screens/restaurant/ProfitAnalysisScreen'
import HourlyDistributionScreen from '@/screens/restaurant/HourlyDistributionScreen'
import CancellationsScreen from '@/screens/restaurant/CancellationsScreen'
import CategoryAnalysisScreen from '@/screens/restaurant/CategoryAnalysisScreen'
import ExpenseScreen from '@/screens/restaurant/ExpenseScreen'
import PurchaseReportScreen from '@/screens/restaurant/PurchaseReportScreen'
import ProfitReportScreen from '@/screens/restaurant/ProfitReportScreen'
import IndentReportScreen from '@/screens/restaurant/IndentReportScreen'
import PnlReportScreen from '@/screens/restaurant/PnlReportScreen'
import SubscriptionScreen from '@/screens/restaurant/SubscriptionScreen'
import CombinedDashboardScreen from '@/screens/restaurant/CombinedDashboardScreen'
import CombinedSalesReportScreen from '@/screens/restaurant/CombinedSalesReportScreen'

// Direct URLs (bookmarks, typed paths) must not reach a page the admin has switched off for
// this restaurant — sending them to the first page that is still available instead.
function FeatureRoute({ feature, children }: { feature: string; children: React.ReactElement }) {
  const { currentRestaurant } = useRestaurant()
  if (isFeatureEnabled(currentRestaurant, feature)) return children
  const fallback = getFirstEnabledPath(currentRestaurant)
  return fallback ? <Navigate to={fallback} replace /> : <NoFeaturesAvailable />
}

function NoFeaturesAvailable() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <h2 className="text-lg font-semibold">No features available</h2>
      <p className="text-sm text-muted-foreground mt-1">
        No features are enabled for this restaurant. Please contact support.
      </p>
    </div>
  )
}

export default function RestaurantNavigator() {
  const { subscriptionStatus } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const homePath = getFirstEnabledPath(currentRestaurant) ?? '/dashboard'

  // Show subscription expired screen if subscription is not active
  if (subscriptionStatus === 'expired' || subscriptionStatus === 'suspended') {
    return <SubscriptionExpiredScreen />
  }

  return (
    <RestaurantLayout>
      <Routes>
        <Route path="/" element={<Navigate to={homePath} replace />} />
        <Route path="/dashboard" element={<FeatureRoute feature="dashboard"><DashboardScreen /></FeatureRoute>} />
        <Route path="/analytics" element={<FeatureRoute feature="analytics"><AnalyticsScreen /></FeatureRoute>} />
        <Route path="/customers" element={<FeatureRoute feature="customers"><CustomersScreen /></FeatureRoute>} />
        <Route path="/sales-reports" element={<FeatureRoute feature="sales-reports"><SalesReportsScreen /></FeatureRoute>} />
        <Route path="/online-orders" element={<FeatureRoute feature="online-orders"><OnlineOrdersScreen /></FeatureRoute>} />
        <Route path="/parcel-takeaway" element={<FeatureRoute feature="parcel-takeaway"><ParcelTakeawayOrdersScreen /></FeatureRoute>} />
        <Route path="/profit-analysis" element={<FeatureRoute feature="profit-analysis"><ProfitAnalysisScreen /></FeatureRoute>} />
        <Route path="/hourly-distribution" element={<FeatureRoute feature="hourly-distribution"><HourlyDistributionScreen /></FeatureRoute>} />
        <Route path="/cancellations" element={<FeatureRoute feature="cancellations"><CancellationsScreen /></FeatureRoute>} />
        <Route path="/edited-bills" element={<FeatureRoute feature="edited-bills"><EditedBillsScreen /></FeatureRoute>} />
        <Route path="/menu-performance" element={<FeatureRoute feature="menu-performance"><MenuPerformanceScreen /></FeatureRoute>} />
        <Route path="/payment-modes" element={<FeatureRoute feature="payment-modes"><PaymentModesScreen /></FeatureRoute>} />
        <Route path="/gst-report" element={<FeatureRoute feature="gst-report"><GstReportScreen /></FeatureRoute>} />
        <Route path="/discounts" element={<FeatureRoute feature="discounts"><DiscountsScreen /></FeatureRoute>} />
        <Route path="/staff-performance" element={<FeatureRoute feature="staff-performance"><StaffPerformanceScreen /></FeatureRoute>} />
        <Route path="/table-performance" element={<FeatureRoute feature="table-performance"><TablePerformanceScreen /></FeatureRoute>} />
        <Route path="/audit-trail" element={<FeatureRoute feature="audit-trail"><AuditTrailScreen /></FeatureRoute>} />
        <Route path="/receivables" element={<FeatureRoute feature="receivables"><ReceivablesScreen /></FeatureRoute>} />
        <Route path="/delivery-performance" element={<FeatureRoute feature="delivery-performance"><DeliveryPerformanceScreen /></FeatureRoute>} />
        <Route path="/z-report" element={<FeatureRoute feature="z-report"><ZReportScreen /></FeatureRoute>} />
        <Route path="/category-analysis" element={<FeatureRoute feature="category-analysis"><CategoryAnalysisScreen /></FeatureRoute>} />
        <Route path="/expenses" element={<FeatureRoute feature="expenses"><ExpenseScreen /></FeatureRoute>} />
        <Route path="/purchase-report" element={<FeatureRoute feature="purchase-report"><PurchaseReportScreen /></FeatureRoute>} />
        <Route path="/profit-report" element={<FeatureRoute feature="profit-report"><ProfitReportScreen /></FeatureRoute>} />
        <Route path="/indent-reports" element={<FeatureRoute feature="indent-reports"><IndentReportScreen /></FeatureRoute>} />
        <Route path="/pnl-report" element={<FeatureRoute feature="pnl-report"><PnlReportScreen /></FeatureRoute>} />
        <Route path="/subscription" element={<FeatureRoute feature="subscription"><SubscriptionScreen /></FeatureRoute>} />
        <Route path="/combined/dashboard" element={<CombinedDashboardScreen />} />
        <Route path="/combined/sales-report" element={<CombinedSalesReportScreen />} />
        <Route path="*" element={<Navigate to={homePath} replace />} />
      </Routes>
    </RestaurantLayout>
  )
}